import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import PricingTierCard from './pricing/PricingTierCard';

const TIERS = [
  {
    plan: 'free', price: 0, nameKey: 'pricing.free.name', subKey: 'pricing.free.sub',
    features: [['pf.minutes', true], ['pf.720p', true], ['pf.captions1', true], ['pf.watermark', true], ['pf.hd1080', false], ['pf.urlimport', false], ['pf.queue', false]],
  },
  {
    plan: 'creator', price: 19, nameKey: 'pricing.pro.name', subKey: 'pricing.pro.sub', highlight: true,
    features: [['pf.unlimited', true], ['pf.urlimport', true], ['pf.hd1080.y', true], ['pf.nowatermark', true], ['pf.multi', true], ['pf.priority', true], ['pf.direct', true]],
  },
  {
    plan: 'studio', price: 49, nameKey: 'pricing.studio.name', subKey: 'pricing.studio.sub',
    features: [['pf.everything', true], ['pf.4k', true], ['pf.team', true], ['pf.brand', true], ['pf.api', true], ['pf.support', true]],
  },
];

const Pricing = () => {
  const { t, lang } = useT();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState('');

  const startCheckout = async (planId) => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    setLoadingPlan(planId);
    try {
      const { data } = await api.post('/payments/checkout/session', {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.assign(data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === 'fr' ? 'Erreur de checkout' : 'Checkout error'));
      // Note: we intentionally do NOT reset loadingPlan on error. The spinner
      // stays on the chosen tier until the user picks another (or refreshes).
    }
  };

  const handleCta = (tier) => {
    if (tier.plan === 'free') {
      navigate(user ? '/dashboard' : '/register');
    } else {
      startCheckout(tier.plan);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="pricing-page">
      <div className="text-center max-w-3xl mx-auto mb-20">
        <span className="text-xs uppercase tracking-[0.25em] text-[#D4FF00] font-mono">{t('pricing.tag')}</span>
        <h1 className="font-heading text-5xl lg:text-6xl font-black mt-4 tracking-tighter leading-[1.05]">
          {t('pricing.title.line1')}<br />{t('pricing.title.line2')}
        </h1>
        <p className="text-zinc-400 mt-5 text-lg">{t('pricing.subtitle')}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
        {TIERS.map((tier, i) => (
          <PricingTierCard
            key={tier.plan}
            tier={tier}
            index={i}
            t={t}
            lang={lang}
            isLoading={loadingPlan === tier.plan}
            disabled={authLoading}
            onCta={() => handleCta(tier)}
          />
        ))}
      </div>

      {/* Reassurance — early adopter transparency */}
      <p
        className="mt-10 text-center text-xs text-zinc-500 max-w-xl mx-auto leading-relaxed"
        data-testid="pricing-reassurance"
      >
        {t('pricing.reassurance')}
      </p>

      <div className="mt-14 text-center text-sm text-zinc-500">
        {t('pricing.custom')} <a className="text-[#D4FF00] hover:underline" href="mailto:contact@highlightai.online">{t('pricing.contact')}</a>.
      </div>
    </div>
  );
};

export default Pricing;
